import type { WSMessage, WSJobSubscribeData, WSExportSubscribeData } from '@/types/ws'
import {
  WSClientMessageType,
  WSServerMessageType,
  WS_API_PATH,
  jobSubscriptionKey,
  exportSubscriptionKey,
} from '@/types/ws'
import type { QueueName } from '@/types/common'

type WSMessageHandler = (message: WSMessage) => void

export type WSConnectionState = 'disconnected' | 'connecting' | 'connected'

class WSClient {
  private static instance: WSClient
  private socket: WebSocket | null = null
  private listeners = new Map<string, Set<WSMessageHandler>>()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private isConnecting = false
  private connectionState: WSConnectionState = 'disconnected'
  private stateListeners = new Set<(state: WSConnectionState) => void>()

  // 当前标签页活跃订阅
  private jobSubscriptions = new Set<string>()
  private exportSubscriptions = new Set<string>()
  private queueSubscriptions = new Set<string>()
  private disconnectRequested = false

  private constructor() {}

  static getInstance(): WSClient {
    if (!WSClient.instance) {
      WSClient.instance = new WSClient()
    }
    return WSClient.instance
  }

  getConnectionState(): WSConnectionState {
    return this.connectionState
  }

  get isConnected(): boolean {
    return this.connectionState === 'connected'
  }

  onStateChange(listener: (state: WSConnectionState) => void): () => void {
    this.stateListeners.add(listener)
    return () => this.stateListeners.delete(listener)
  }

  private setConnectionState(state: WSConnectionState): void {
    this.connectionState = state
    this.stateListeners.forEach((fn) => fn(state))
  }

  private getWsUrl(): string {
    if (typeof window === 'undefined') return ''
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//${window.location.host}${WS_API_PATH}`
  }

  connect(): void {
    if (typeof window === 'undefined') return

    const token = localStorage.getItem('token')
    if (!token) return

    const state = this.socket?.readyState
    // 已有连接、正在连接或正在关闭时不再重复创建（避免重连瞬间产生孤儿连接）
    if (
      state === WebSocket.OPEN ||
      state === WebSocket.CONNECTING ||
      state === WebSocket.CLOSING ||
      this.isConnecting
    ) {
      return
    }

    this.disconnectRequested = false
    this.isConnecting = true
    this.setConnectionState('connecting')

    try {
      this.socket = new WebSocket(this.getWsUrl())

      this.socket.onopen = () => {
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.send({ type: WSClientMessageType.AUTH, data: { token } })
      }

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WSMessage
          this.handleMessage(message)
        } catch (e) {
          console.error('[WS] Failed to parse message:', e)
        }
      }

      this.socket.onerror = () => {
        // onclose 会处理重连
      }

      this.socket.onclose = () => {
        this.socket = null
        this.isConnecting = false
        this.setConnectionState('disconnected')
        if (!this.disconnectRequested) {
          this.scheduleReconnect()
        }
      }
    } catch (e) {
      console.error('[WS] Failed to connect:', e)
      this.isConnecting = false
      this.setConnectionState('disconnected')
      this.scheduleReconnect()
    }
  }

  disconnect(): void {
    this.disconnectRequested = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempts = 0
    if (this.socket) {
      const ws = this.socket
      this.socket = null
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      } else if (ws.readyState === WebSocket.CONNECTING) {
        // 连接尚未建立时直接 close 会触发浏览器警告，等 open 后再关闭
        ws.onopen = () => ws.close()
      }
    }
    this.isConnecting = false
    this.setConnectionState('disconnected')
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return
    if (typeof window === 'undefined') return
    if (!localStorage.getItem('token')) return

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)
    this.reconnectAttempts += 1

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  private send(message: WSMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message))
    }
  }

  private handleMessage(message: WSMessage): void {
    if (message.type === WSServerMessageType.CONNECTED) {
      this.setConnectionState('connected')
      this.resubscribeAll()
      this.dispatch('connected', message)
    }

    if (message.type === WSServerMessageType.PING) {
      this.send({ type: WSClientMessageType.PONG, data: {} })
    }

    this.dispatch('message', message)
    this.dispatch(message.type, message)
  }

  private resubscribeAll(): void {
    this.jobSubscriptions.forEach((key) => {
      const sepIdx = key.lastIndexOf(':')
      if (sepIdx === -1) return
      const queueName = key.slice(0, sepIdx)
      const jobId = key.slice(sepIdx + 1)
      this.send({
        type: WSClientMessageType.JOB_SUBSCRIBE,
        data: { job_id: jobId, queue_name: queueName },
      })
    })

    this.exportSubscriptions.forEach((taskId) => {
      this.send({
        type: WSClientMessageType.EXPORT_SUBSCRIBE,
        data: { task_id: taskId },
      })
    })

    this.queueSubscriptions.forEach((queueName) => {
      this.send({
        type: WSClientMessageType.QUEUE_SUBSCRIBE,
        data: { queue_name: queueName },
      })
    })
  }

  subscribeJob(jobId: string | number, queueName: QueueName | string): void {
    const normalizedJobId = String(jobId)
    const key = jobSubscriptionKey(queueName, normalizedJobId)
    this.jobSubscriptions.add(key)
    if (this.isConnected) {
      this.send({
        type: WSClientMessageType.JOB_SUBSCRIBE,
        data: { job_id: normalizedJobId, queue_name: queueName } satisfies WSJobSubscribeData,
      })
    } else {
      this.connect()
    }
  }

  unsubscribeJob(jobId: string | number, queueName: QueueName | string): void {
    const normalizedJobId = String(jobId)
    const key = jobSubscriptionKey(queueName, normalizedJobId)
    this.jobSubscriptions.delete(key)
    if (this.isConnected) {
      this.send({
        type: WSClientMessageType.JOB_UNSUBSCRIBE,
        data: { job_id: normalizedJobId, queue_name: queueName },
      })
    }
  }

  subscribeQueue(queueName: QueueName | string): void {
    this.queueSubscriptions.add(queueName)
    if (this.isConnected) {
      this.send({
        type: WSClientMessageType.QUEUE_SUBSCRIBE,
        data: { queue_name: queueName },
      })
    } else {
      this.connect()
    }
  }

  unsubscribeQueue(queueName: QueueName | string): void {
    this.queueSubscriptions.delete(queueName)
    if (this.isConnected) {
      this.send({
        type: WSClientMessageType.QUEUE_UNSUBSCRIBE,
        data: { queue_name: queueName },
      })
    }
  }

  subscribeExport(taskId: string): void {
    this.exportSubscriptions.add(taskId)
    if (this.isConnected) {
      this.send({
        type: WSClientMessageType.EXPORT_SUBSCRIBE,
        data: { task_id: taskId } satisfies WSExportSubscribeData,
      })
    } else {
      this.connect()
    }
  }

  unsubscribeExport(taskId: string): void {
    this.exportSubscriptions.delete(taskId)
    if (this.isConnected) {
      this.send({
        type: WSClientMessageType.EXPORT_UNSUBSCRIBE,
        data: { task_id: taskId },
      })
    }
  }

  on(event: string, handler: WSMessageHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
    return () => this.off(event, handler)
  }

  off(event: string, handler: WSMessageHandler): void {
    const set = this.listeners.get(event)
    if (set) {
      set.delete(handler)
      if (set.size === 0) this.listeners.delete(event)
    }
  }

  private dispatch(event: string, message: WSMessage): void {
    const handlers = this.listeners.get(event)
    if (!handlers) return
    handlers.forEach((handler) => {
      try {
        handler(message)
      } catch (e) {
        console.error(`[WS] Handler error for ${event}:`, e)
      }
    })
  }

  onReconnect(handler: () => void): () => void {
    return this.on('connected', handler)
  }
}

export const wsClient = WSClient.getInstance()
